/**
 * Public reference data for Dhruv Consultancy Services Ltd (DCSL).
 * Sources: BSE/NSE filings, MCA-style CIN, company website & investor materials (2024–25).
 * Used to prefill company onboarding — verify before production legal use.
 */
export const DHRUV_DCSL_CORPORATE_DEFAULTS = {
  legalName: 'Dhruv Consultancy Services Ltd',
  displayName: 'Dhruv Consultancy',
  code: 'DCSL',
  industry: 'Infrastructure Consultancy',
  companySize: 'LARGE',
  description:
    'ISO 9001:2015 certified infrastructure consultancy delivering strategic expertise and engineering solutions across India and international markets. Listed on the main boards of BSE and NSE.',
  contact: {
    email: 'info@dhruvconsultancy.in',
    phone: '+91 22 27570710',
    website: 'https://www.dhruvconsultancy.in',
    address: {
      street: '501, Pujit Plaza, Palm Beach Road, Sector 11, Opp. K-Star Hotel, Near CBD Station, CBD Belapur',
      city: 'Navi Mumbai',
      state: 'Maharashtra',
      postalCode: '400614',
      country: 'India'
    }
  },
  corporateProfile: {
    cin: 'L74999MH2003PLC141887',
    incorporationDate: '2003-08-26',
    lei: '8945006PFKNKXJVMH984',
    isin: 'INE506Z01015',
    rocJurisdiction: 'RoC-Mumbai',
    isoCertification: 'ISO 9001:2015',
    bseSecurityCode: '541302',
    bseSecurityId: 'DHRUV',
    nseSymbol: 'DHRUV',
    faceValueInr: 10,
    listedOnBse: true,
    listedOnNse: true,
    registrarName: 'MUFG Intime India Pvt Ltd.',
    secondaryEmails: {
      services: 'services@dhruvconsultancy.in',
      companySecretary: 'cs@dhruvconsultancy.in',
      investorRelations: 'cs@dhruvconsultancy.in'
    }
  },
  metadata: {
    foundedYear: 2003,
    legalEntityType: 'PUBLIC_LIMITED',
    compliance: {
      gstRegistered: true,
      gstNumber: '',
      panNumber: ''
    }
  }
};

export function mergeOrganisationFromCompany(company, draftOrg = {}) {
  const cp = company?.corporateProfile || {};
  const meta = company?.metadata || {};
  const contact = company?.contact || {};
  const addr = contact.address || {};
  const sec = cp.secondaryEmails || {};

  return {
    legalName: draftOrg.legalName ?? company?.legalName ?? company?.name ?? '',
    displayName: draftOrg.displayName ?? company?.displayName ?? '',
    code: draftOrg.code ?? company?.code ?? '',
    industry: draftOrg.industry ?? company?.industry ?? '',
    companySize: draftOrg.companySize ?? company?.companySize ?? 'MEDIUM',
    description: draftOrg.description ?? company?.description ?? '',
    contact: {
      email: draftOrg.contact?.email ?? contact.email ?? '',
      phone: draftOrg.contact?.phone ?? contact.phone ?? '',
      website: draftOrg.contact?.website ?? contact.website ?? '',
      address: {
        street: draftOrg.contact?.address?.street ?? addr.street ?? '',
        city: draftOrg.contact?.address?.city ?? addr.city ?? '',
        state: draftOrg.contact?.address?.state ?? addr.state ?? '',
        postalCode: draftOrg.contact?.address?.postalCode ?? addr.postalCode ?? '',
        country: draftOrg.contact?.address?.country ?? addr.country ?? 'India'
      }
    },
    corporateProfile: {
      cin: draftOrg.corporateProfile?.cin ?? cp.cin ?? meta.registrationNumber ?? '',
      incorporationDate: draftOrg.corporateProfile?.incorporationDate
        ?? (cp.incorporationDate ? String(cp.incorporationDate).slice(0, 10) : ''),
      lei: draftOrg.corporateProfile?.lei ?? cp.lei ?? '',
      isin: draftOrg.corporateProfile?.isin ?? cp.isin ?? '',
      rocJurisdiction: draftOrg.corporateProfile?.rocJurisdiction ?? cp.rocJurisdiction ?? '',
      isoCertification: draftOrg.corporateProfile?.isoCertification ?? cp.isoCertification ?? '',
      bseSecurityCode: draftOrg.corporateProfile?.bseSecurityCode ?? cp.bseSecurityCode ?? '',
      bseSecurityId: draftOrg.corporateProfile?.bseSecurityId ?? cp.bseSecurityId ?? '',
      nseSymbol: draftOrg.corporateProfile?.nseSymbol ?? cp.nseSymbol ?? '',
      faceValueInr: draftOrg.corporateProfile?.faceValueInr ?? cp.faceValueInr ?? '',
      listedOnBse: draftOrg.corporateProfile?.listedOnBse ?? cp.listedOnBse ?? false,
      listedOnNse: draftOrg.corporateProfile?.listedOnNse ?? cp.listedOnNse ?? false,
      registrarName: draftOrg.corporateProfile?.registrarName ?? cp.registrarName ?? '',
      secondaryEmails: {
        services: draftOrg.corporateProfile?.secondaryEmails?.services ?? sec.services ?? '',
        companySecretary: draftOrg.corporateProfile?.secondaryEmails?.companySecretary ?? sec.companySecretary ?? '',
        investorRelations: draftOrg.corporateProfile?.secondaryEmails?.investorRelations ?? sec.investorRelations ?? ''
      }
    },
    metadata: {
      foundedYear: draftOrg.metadata?.foundedYear ?? meta.foundedYear ?? '',
      legalEntityType: draftOrg.metadata?.legalEntityType ?? meta.legalEntityType ?? 'PRIVATE_LIMITED',
      compliance: {
        gstRegistered: draftOrg.metadata?.compliance?.gstRegistered ?? meta.compliance?.gstRegistered ?? false,
        gstNumber: draftOrg.metadata?.compliance?.gstNumber ?? meta.compliance?.gstNumber ?? '',
        panNumber: draftOrg.metadata?.compliance?.panNumber ?? meta.compliance?.panNumber ?? ''
      }
    }
  };
}

export function applyDhruvDefaultsIfEmpty(org, companyCode) {
  if (String(companyCode || '').toUpperCase() !== 'DCSL') return org;
  const d = DHRUV_DCSL_CORPORATE_DEFAULTS;
  const filled = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  const out = mergeOrganisationFromCompany({}, org);
  if (!filled(out.legalName)) out.legalName = d.legalName;
  if (!filled(out.displayName)) out.displayName = d.displayName;
  if (!filled(out.code)) out.code = d.code;
  if (!filled(out.industry)) out.industry = d.industry;
  if (!filled(out.description)) out.description = d.description;
  if (!filled(out.companySize)) out.companySize = d.companySize;
  out.contact = {
    ...d.contact,
    ...out.contact,
    address: { ...d.contact.address, ...out.contact.address }
  };
  out.corporateProfile = {
    ...d.corporateProfile,
    ...out.corporateProfile,
    secondaryEmails: {
      ...d.corporateProfile.secondaryEmails,
      ...out.corporateProfile.secondaryEmails
    }
  };
  out.metadata = {
    ...d.metadata,
    ...out.metadata,
    compliance: { ...d.metadata.compliance, ...out.metadata.compliance }
  };
  return out;
}
